import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Promotion } from '@/db/models/System';
import { toPaisa } from './billingEngine';

export interface PromotionResult {
  valid: boolean;
  promotion?: Promotion;
  discountPaisa: number;
  error?: string;
}

export async function validatePromoCode(code: string, merchantId: string, cartTotalPaisa: number): Promise<PromotionResult> {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return { valid: false, discountPaisa: 0, error: 'Enter a coupon code.' };
  const promos = await database.get<Promotion>('promotions').query(
    Q.where('merchant_id', merchantId),
    Q.where('code', trimmed),
    Q.where('is_deleted', false),
  ).fetch();
  if (promos.length === 0) return { valid: false, discountPaisa: 0, error: 'Invalid coupon code.' };
  return validatePromotion(promos[0], cartTotalPaisa);
}

export function validatePromotion(promo: Promotion, cartTotalPaisa: number): PromotionResult {
  const now = new Date();
  if (!promo.isActive) return { valid: false, discountPaisa: 0, error: 'Promotion is inactive.' };
  if (now < promo.startDate) return { valid: false, discountPaisa: 0, error: 'Promotion has not started.' };
  if (now > promo.endDate) return { valid: false, discountPaisa: 0, error: 'Promotion has expired.' };
  if (promo.usageLimit && promo.usedCount >= promo.usageLimit) {
    return { valid: false, discountPaisa: 0, error: 'Promotion usage limit reached.' };
  }
  if (promo.minOrderValue && cartTotalPaisa < toPaisa(promo.minOrderValue)) {
    return { valid: false, discountPaisa: 0, error: `Minimum order ${promo.minOrderValue} required.` };
  }

  let discountPaisa = 0;
  if (promo.type === 'PERCENTAGE') {
    discountPaisa = Math.floor(cartTotalPaisa * promo.value / 100);
    if (promo.maxDiscount) discountPaisa = Math.min(discountPaisa, toPaisa(promo.maxDiscount));
  } else {
    discountPaisa = Math.min(cartTotalPaisa, toPaisa(promo.value));
  }

  return { valid: true, promotion: promo, discountPaisa };
}

/**
 * Prepare the usage bump for an already-loaded promotion.
 *
 * Returns a prepared update rather than committing one, so the checkout writer
 * can apply it in the same `database.batch()` as the invoice. A promotion whose
 * usage counted against a sale that never landed would slowly exhaust a coupon
 * nobody redeemed.
 */
export function preparePromoUsageIncrement(promo: Promotion): Promotion {
  return promo.prepareUpdate((record) => {
    record.usedCount += 1;
    record.lastSyncUsedCount = null;
    record.version += 1;
    record.isSynced = false;
  });
}
